import React, { ReactNode, useEffect, useRef, useState } from "react";
import { TableField } from "../../../../types/table-field";
import { translations } from "../../../../utils/translations/translations-manager";

interface TableProps {
  tableFields: TableField[]
  children: ReactNode
  additionalEntry?: ReactNode
  additionalEntryWidth?: number
  additionalEntryPosition?: boolean // true for first, false for last
}

const TableStructure: React.FC<TableProps> = ({ tableFields, children, additionalEntry, additionalEntryWidth, additionalEntryPosition = false }) => {
  const [columnWidths, setColumnWidths] = useState<number[]>([])
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  const tableRef = useRef<HTMLTableElement>(null)

  const handleMouseDown = (index: number, event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (isMobile) return

    const startX = event.clientX
    const startWidth = columnWidths[index]
  
    const handleMouseMove = (moveEvent: MouseEvent) => {
      const newWidth = Math.max(70, startWidth + (moveEvent.clientX - startX))
      setColumnWidths((prevWidths) =>
        prevWidths.map((width, i) => (i === index ? newWidth : width))
      )
    }
  
    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove)
      document.removeEventListener("mouseup", handleMouseUp)
    }
    
    document.addEventListener("mousemove", handleMouseMove)
    document.addEventListener("mouseup", handleMouseUp)
  }

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  useEffect(() => {
    if (tableRef.current && !isMobile) {
      const containerWidth = tableRef.current.offsetWidth - (additionalEntry && additionalEntryWidth ? additionalEntryWidth : 0)
      const columnWidth = Math.max(70, (containerWidth / tableFields.length - 2))
      setColumnWidths(tableFields.map(() => columnWidth))
    }
  }, [tableFields, isMobile])

  return (
    <>
      <div className="table-container overflow-x-auto relative rounded-2xl">
        <table ref={tableRef} className="relative w-full table-fixed">
          <thead className="h-14 w-full sticky top-0 bg-cents-purple-lighter">
            <tr className="text-cents-black min-w-full text-sm 2xl:text-base">
              {additionalEntryPosition && additionalEntry}
              {tableFields.map((col, index) => (
                <th 
                  key={index} 
                  className={`${col.name === '_id' && !additionalEntryPosition ? '': 'border-short'} relative pl-4 pr-8 h-16 font-semibold text-left whitespace-nowrap truncate`} 
                  style={{ width: isMobile ? "270px" : `${columnWidths[index]}px` }}>
                  {col.label ? translations.getText(col.label) : ''}
                  <div className="hidden md:flex absolute right-0 top-0 h-full w-8 cursor-col-resize items-center" onMouseDown={(e) => handleMouseDown(index, e)}>
                    <svg className="h-5 w-5 group" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M13.9998 12H18.9998M9.99985 12H4.99985M15.9998 8L19.2927 11.2929C19.6833 11.6834 19.6833 12.3166 19.2927 12.7071L15.9998 16M7.99985 8L4.70696 11.2929C4.31643 11.6834 4.31643 12.3166 4.70696 12.7071L7.99985 16"
                        className="group-hover:stroke-cents-black"
                        stroke="gray"
                        strokeWidth="2" 
                        strokeLinecap="round"/>
                    </svg>
                  </div>
                </th>
              ))}
              {!additionalEntryPosition && additionalEntry}
            </tr>
          </thead>
          <tbody className="h-fit w-full">
            {children}
          </tbody>
        </table>
      </div>
    </>
  )
}

export default TableStructure;